/**
 * Privacy Policy Page
 * Required for Google AdSense compliance
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography, Stack, Divider, Link as MuiLink } from '@mui/material';
import { trackPageView } from '../utils/analytics';

export default function Privacy() {
  useCustomTranslation();

  useEffect(() => {
    trackPageView('/privacy', 'Privacy Policy');
    document.title = 'Privacy Policy | PayWise365';
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            Your privacy is important to us
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <strong>Last Updated:</strong> February 7, 2026
            </Typography>
          </Box>

          {/* Introduction */}
          <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                1. Introduction
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Welcome to PayWise365.com ("we", "us", "our"). We operate www.paywise365.com, 
                a free loan and mortgage calculator for Hong Kong residents. This Privacy Policy 
                explains how we collect, use, and protect your information.
              </Typography>
            </Box>

            <Divider />

            {/* Information We Collect */}
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                2. Information We Collect
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    2.1 Calculator Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    When you use our calculators, we process loan information (amounts, interest rates, 
                    terms, payment amounts) <strong>locally in your browser</strong>. We send this data 
                    to our backend API for calculations, but we <strong>do not store or save</strong> 
                    your personal loan details.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    2.2 Cookies and Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                    We use cookies and similar technologies for:
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                    <li><Typography variant="body2" color="text.secondary" component="span"><strong>Google Analytics:</strong> To understand how visitors use our site (pages visited, time spent, etc.)</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span"><strong>Google AdSense:</strong> To display relevant advertisements</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span"><strong>Language Preference:</strong> To remember your selected language</Typography></li>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    2.3 Automatically Collected Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                    We may collect:
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                    <li><Typography variant="body2" color="text.secondary" component="span">IP address (anonymized)</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span">Browser type and version</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span">Device type (desktop, mobile, tablet)</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span">Pages visited and time spent</Typography></li>
                    <li><Typography variant="body2" color="text.secondary" component="span">Referral source (where you came from)</Typography></li>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* How We Use Your Information */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                3. How We Use Your Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                We use collected information to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li><Typography variant="body2" color="text.secondary" component="span">Provide and improve our calculator services</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">Analyze site usage and performance</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">Display relevant advertisements</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">Detect and prevent fraud or abuse</Typography></li>
              </Box>
            </Box>

            <Divider />

            {/* Google AdSense */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                4. Google AdSense
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                We use <strong>Google AdSense</strong> to display advertisements on our site. 
                Google uses cookies to serve ads based on your prior visits to our website or 
                other websites on the Internet.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
                  How Google Uses Your Data:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li><Typography variant="body2" color="text.secondary" component="span">Google may collect information about your visits to this and other websites</Typography></li>
                  <li><Typography variant="body2" color="text.secondary" component="span">Google uses this information to provide targeted advertisements</Typography></li>
                  <li><Typography variant="body2" color="text.secondary" component="span">Google's advertising cookies enable personalized ads across the web</Typography></li>
                </Box>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
                  Opt Out of Personalized Ads:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                  You can opt out of personalized advertising by visiting:{' '}
                  <MuiLink href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                    Google Ads Settings
                  </MuiLink>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                  Alternatively, you can opt out of third-party cookies by visiting:{' '}
                  <MuiLink href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
                    Digital Advertising Alliance Opt-Out
                  </MuiLink>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Learn more about how Google uses data:{' '}
                  <MuiLink href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
                    Google Privacy & Terms
                  </MuiLink>
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Google Analytics */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                5. Google Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                We use <strong>Google Analytics</strong> to analyze site traffic. Google Analytics 
                uses cookies to help us understand how visitors interact with our site.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                The information collected includes: pages viewed, time on site, bounce rate, and 
                demographic information (age, gender, interests).
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                Google Analytics Privacy Policy:{' '}
                <MuiLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  https://policies.google.com/privacy
                </MuiLink>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                You can opt out of Google Analytics by installing the{' '}
                <MuiLink href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Browser Add-on
                </MuiLink>
              </Typography>
            </Box>

            <Divider />

            {/* Affiliate Disclosure */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                6. Affiliate Disclosure
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                Some pages on PayWise365 contain <strong>affiliate links</strong>. This means that if 
                you click on a link and sign up for a product or make a purchase, we may receive a 
                small commission at <strong>no additional cost to you</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                As an <strong>Amazon Associate</strong>, PayWise365 earns from qualifying purchases. 
                We may also participate in affiliate programs with financial comparison platforms and 
                banking partners.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                Our affiliate relationships do <strong>not</strong> influence:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body2" color="text.secondary" component="span">The interest rates or data we display</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">The order or ranking of banks in our comparison tables</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">Our editorial opinions or recommendations</Typography></li>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We only recommend products and services we believe are genuinely useful for our users. 
                Affiliate income helps us keep PayWise365 free for everyone.
              </Typography>
            </Box>

            <Divider />

            {/* Third-Party Services */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                7. Third-Party Services
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                We use the following third-party services:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li>
                  <Typography variant="body2" color="text.secondary" component="span">
                    <strong>Amazon Web Services (AWS):</strong> For hosting our website and API{' '}
                    <MuiLink href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">
                      (AWS Privacy Policy)
                    </MuiLink>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary" component="span">
                    <strong>Google Analytics:</strong> For traffic analysis{' '}
                    <MuiLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                      (Privacy Policy)
                    </MuiLink>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary" component="span">
                    <strong>Google AdSense:</strong> For advertising{' '}
                    <MuiLink href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
                      (Privacy Policy)
                    </MuiLink>
                  </Typography>
                </li>
              </Box>
            </Box>

            <Divider />

            {/* Data Storage and Security */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                8. Data Storage and Security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                <strong>Important:</strong> We do NOT store your personal loan calculation data. 
                All calculations are processed in real-time and results are only shown to you in 
                your browser.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Analytics data (cookies, pageviews) is stored by Google and governed by their privacy policies.
              </Typography>
            </Box>

            <Divider />

            {/* Your Rights */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                9. Your Rights
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                You have the right to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Access:</strong> Request what data we have about you</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Deletion:</strong> Request deletion of your data (cookies)</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Opt-Out:</strong> Disable cookies in your browser settings</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Opt-Out of Ads:</strong> Use Google Ads Settings to disable personalized ads</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Portability:</strong> Request a copy of your data (if applicable)</Typography></li>
              </Box>
            </Box>

            <Divider />

            {/* Children's Privacy */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                10. Children's Privacy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Our service is not intended for children under 18. We do not knowingly collect 
                personal information from children. If you are a parent and believe your child 
                has provided us with information, please contact us.
              </Typography>
            </Box>

            <Divider />

            {/* International Users */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                11. International Users
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Our servers are located in the United States (AWS us-east-1 region). By using 
                our site, you consent to the transfer of your data to the United States.
              </Typography>
            </Box>

            <Divider />

            {/* Changes to Privacy Policy */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                12. Changes to This Privacy Policy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                We may update this Privacy Policy from time to time. We will notify you of any 
                changes by posting the new Privacy Policy on this page and updating the "Last 
                Updated" date.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We encourage you to review this Privacy Policy periodically for any changes.
              </Typography>
            </Box>

            <Divider />

            {/* Contact Us */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                13. Contact Us
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Email:</strong> privacy@paywise365.com</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span"><strong>Website:</strong> www.paywise365.com</Typography></li>
              </Box>
            </Box>

            <Divider />

            {/* Disclaimer */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                14. Disclaimer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                <strong>Financial Disclaimer:</strong> PayWise365 is for informational purposes only. 
                We do not provide financial, legal, or tax advice. The calculators are tools to 
                estimate loan payments and should not be considered professional financial advice. 
                Always consult with a licensed financial advisor for personalized guidance.
              </Typography>
            </Box>

            <Divider />

            {/* Compliance */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This privacy policy complies with:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                <li><Typography variant="body2" color="text.secondary" component="span">Personal Data (Privacy) Ordinance (PDPO) - Hong Kong</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">Google AdSense Publisher Policies</Typography></li>
                <li><Typography variant="body2" color="text.secondary" component="span">General Data Protection Regulation (GDPR) - For EU visitors</Typography></li>
              </Box>
            </Box>
          </Stack>
        </Container>
    </Box>
  );
}
